import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { OreDepositData, OrePagination, OreSummary, RefineResponse } from '~/composables/api/useOreApi'
import { fetchOre, refineOre as refineOreApi } from '~/composables/api/useOreApi'
import { getApiErrorMessage } from '~/utils/getApiErrorMessage'

export const ORE_DEPOSITS_PAGE_SIZE = 8

export const useOreStore = defineStore('ore', () => {
  // State
  const deposits = ref<OreDepositData[]>([])
  const summary = ref<OreSummary>({
    totalLockedOre: 0,
    totalAvailableOre: 0,
    totalRefinedAllTime: 0,
    lockedDepositsCount: 0,
    availableDepositsCount: 0,
  })
  const isLoading = ref(false)
  const hasLoaded = ref(false)
  const isRefining = ref(false)
  const error = ref<string | null>(null)
  const lastRefineResult = ref<RefineResponse | null>(null)
  const rakebackVisible = ref(true)
  const pagination = ref<OrePagination>({
    page: 1,
    limit: ORE_DEPOSITS_PAGE_SIZE,
    total: 0,
  })

  // Getters
  const lockedDeposits = computed(() =>
    deposits.value.filter(d => d.status === 'locked')
  )

  const availableDeposits = computed(() =>
    deposits.value.filter(d => d.status === 'available')
  )

  const totalLocked = computed(() => summary.value.totalLockedOre)
  const totalAvailable = computed(() => summary.value.totalAvailableOre)
  const totalRefined = computed(() => summary.value.totalRefinedAllTime)

  const hasAvailableOre = computed(() => summary.value.totalAvailableOre > 0)
  const hasAnyOre = computed(() =>
    summary.value.totalLockedOre > 0 || summary.value.totalAvailableOre > 0
  )

  /** Earliest activation time among locked deposits */
  const nextActivationTime = computed(() => {
    if (lockedDeposits.value.length === 0) return null
    const earliest = lockedDeposits.value.reduce((min, d) =>
      d.activatesAt < min.activatesAt ? d : min
    )
    return earliest.activatesAt
  })

  /** Earliest expiry time among available deposits */
  const nextExpiryTime = computed(() => {
    if (availableDeposits.value.length === 0) return null
    const earliest = availableDeposits.value.reduce((min, d) =>
      d.expiresAt < min.expiresAt ? d : min
    )
    return earliest.expiresAt
  })

  // Actions
  let fetchSeq = 0

  async function fetchOreData(
    accessToken?: string,
    options: { page?: number; limit?: number } = {},
  ): Promise<void> {
    if (!accessToken) return

    const reqId = ++fetchSeq
    isLoading.value = true
    error.value = null

    const page = options.page ?? pagination.value.page
    const limit = options.limit ?? pagination.value.limit

    try {
      const response = await fetchOre(accessToken, { page, limit })
      // Drop responses from outdated requests so they don't overwrite newer data
      if (reqId !== fetchSeq) return
      deposits.value = response.deposits
      summary.value = response.summary
      if (response.visible !== undefined) rakebackVisible.value = response.visible
      if (response.pagination) pagination.value = response.pagination
      hasLoaded.value = true
    } catch (err) {
      if (reqId !== fetchSeq) return
      console.error('[oreStore] Failed to fetch ore:', err)
      error.value = getApiErrorMessage(err, 'Failed to fetch ore data')
    } finally {
      if (reqId === fetchSeq) isLoading.value = false
    }
  }

  async function refineOre(accessToken: string): Promise<RefineResponse | null> {
    isRefining.value = true
    error.value = null
    lastRefineResult.value = null

    try {
      const result = await refineOreApi(accessToken)
      lastRefineResult.value = result

      if (result.success) {
        // Refresh ore data after successful refine — reset to page 1
        await fetchOreData(accessToken, { page: 1 })
      }

      return result
    } catch (err) {
      console.error('[oreStore] Failed to refine ore:', err)
      error.value = getApiErrorMessage(err, 'Failed to refine ore')
      return null
    } finally {
      isRefining.value = false
    }
  }

  function clearOre(): void {
    deposits.value = []
    summary.value = {
      totalLockedOre: 0,
      totalAvailableOre: 0,
      totalRefinedAllTime: 0,
      lockedDepositsCount: 0,
      availableDepositsCount: 0,
    }
    error.value = null
    lastRefineResult.value = null
    pagination.value = { page: 1, limit: ORE_DEPOSITS_PAGE_SIZE, total: 0 }
    hasLoaded.value = false
  }

  return {
    // State
    deposits,
    summary,
    isLoading,
    hasLoaded,
    isRefining,
    error,
    lastRefineResult,
    rakebackVisible,
    pagination,
    // Getters
    lockedDeposits,
    availableDeposits,
    totalLocked,
    totalAvailable,
    totalRefined,
    hasAvailableOre,
    hasAnyOre,
    nextActivationTime,
    nextExpiryTime,
    // Actions
    fetchOreData,
    refineOre,
    clearOre,
  }
})
